import { z } from 'zod';
import {
  PCMP_DISBURSEMENT_CONFIRM_STATUSES,
  PCMP_DISBURSEMENT_VERIFY_STATUSES,
  pcmpPayoutAcksSchema,
  pcmpStatusViewSchema,
  type PcmpDisbursementConfirmStatus,
  type PcmpDisbursementVerifyStatus,
} from './pcmpProposal.types';

// The PCMP division of the MO's department, not the geo division.
export const PCMP_DIVISIONS = ['CNS', 'CTG', 'RAJSHAHI', 'MYMENSINGH', 'DHAKA', 'KHULNA'] as const;
export type PcmpDivision = (typeof PCMP_DIVISIONS)[number];

const idSchema = z.coerce.number();

export const pcmpQueuePersonSchema = z.object({
  userId: idSchema,
  fullName: z.string(),
  hrEmpId: z.string().nullable(),
  designationName: z.string().nullable(),
  mobile: z.string().nullable(),
  isActive: z.boolean(),
});

export type PcmpQueuePerson = z.infer<typeof pcmpQueuePersonSchema>;

/** The proposal row every PCMP queue (Marketing, MIS) shares. */
export const pcmpQueueItemSchema = z.object({
  proposalId: idSchema,
  refNo: z.string(),
  proposalDate: z.string(),
  projectId: idSchema,
  projectName: z.string(),
  mo: pcmpQueuePersonSchema.nullable(),
  reporting: pcmpQueuePersonSchema.nullable(),
  institute: z.object({
    id: idSchema,
    name: z.string().nullable(),
    eiinNumber: z.string().nullable(),
    thanaName: z.string().nullable(),
    districtName: z.string().nullable(),
  }),
  teacher: z.object({
    name: z.string(),
    mobile: z.string().nullable(),
    designation: z.string().nullable(),
  }),
  proposedAmount: z.number(),
  teacherDue: z.number().nullable(),
  routeBatchNo: z.number().nullable(),
  urgentBatchNo: z.number().nullable(),
  status: pcmpStatusViewSchema,
  payout: pcmpPayoutAcksSchema,
  hold: z
    .object({
      stageLabel: z.string().nullable(),
      reason: z.string().nullable(),
      heldAt: z.string().nullable(),
      heldByUserId: idSchema.nullable(),
    })
    .nullable(),
  /** The viewer may put it on hold now (LB-772). */
  canHold: z.boolean(),
  /** The viewer may release its hold. */
  canRelease: z.boolean(),
  misNotVerified: z
    .object({
      reasonLabel: z.string().nullable(),
      explanation: z
        .object({
          text: z.string().nullable(),
          by: pcmpQueuePersonSchema.nullable(),
          at: z.string().nullable(),
        })
        .nullable(),
    })
    .nullable(),
});

export type PcmpQueueItem = z.infer<typeof pcmpQueueItemSchema>;
export type PcmpMisNotVerified = NonNullable<PcmpQueueItem['misNotVerified']>;

export const pcmpPageMetaSchema = z.object({
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});

/** A proposal a bulk batch action left out, and why. */
export const pcmpSkippedProposalSchema = z.object({
  proposalId: z.coerce.number(),
  reason: z.string(),
});

export type PcmpSkippedProposal = z.infer<typeof pcmpSkippedProposalSchema>;

/** The filters every PCMP queue shares; the counts take these alone. */
export interface PcmpQueueFilters {
  projectId?: number | undefined;
  departmentId?: number | undefined;
  division?: PcmpDivision | undefined;
  refNo?: string | undefined;
  teacher?: string | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
  status?: string | undefined;
  reason?: string | undefined;
  batchNo?: number | undefined;
  accDisbConfirm?: PcmpDisbursementConfirmStatus | undefined;
  disbVerify?: PcmpDisbursementVerifyStatus | undefined;
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

const positiveInt = z.number().int().positive().optional().catch(undefined);

/**
 * The URL search fields of a queue's filters and paging; each queue route adds
 * its own `tab`. The status lives in the URL as `resolvedStatus`.
 */
export const pcmpQueueSearchShape = {
  page: positiveInt,
  limit: z.number().int().min(1).max(100).optional().catch(undefined),
  projectId: positiveInt,
  departmentId: positiveInt,
  division: z.enum(PCMP_DIVISIONS).optional().catch(undefined),
  refNo: z.string().max(50).optional().catch(undefined),
  teacher: z.string().max(100).optional().catch(undefined),
  dateFrom: z.string().regex(DATE_ONLY_RE).optional().catch(undefined),
  dateTo: z.string().regex(DATE_ONLY_RE).optional().catch(undefined),
  resolvedStatus: z.string().optional().catch(undefined),
  reason: z.string().optional().catch(undefined),
  batchNo: positiveInt,
  accDisbConfirm: z.enum(PCMP_DISBURSEMENT_CONFIRM_STATUSES).optional().catch(undefined),
  disbVerify: z.enum(PCMP_DISBURSEMENT_VERIFY_STATUSES).optional().catch(undefined),
};

export const pcmpQueueSearchSchema = z.object(pcmpQueueSearchShape);

export type PcmpQueueSearch = z.infer<typeof pcmpQueueSearchSchema>;

const codeLabelSchema = z.object({ code: z.string(), label: z.string() });

export type PcmpCodeLabel = z.infer<typeof codeLabelSchema>;

export const pcmpStatusCatalogSchema = z.object({
  resolvedStatuses: z.array(codeLabelSchema),
  misReasons: z.array(codeLabelSchema),
  accountsReasons: z.array(codeLabelSchema),
  /** Options of the accDisbConfirm / disbVerify filters (LB-1195). */
  disbursementConfirmStatuses: z.array(codeLabelSchema),
  disbursementVerifyStatuses: z.array(codeLabelSchema),
});

export type PcmpStatusCatalog = z.infer<typeof pcmpStatusCatalogSchema>;
